import React, { useState, useMemo } from 'react';
import { RequisitionItem } from '../../types';
import { ItemRow, statusConfig, rowKey } from './requisitionWorkflowHelpers';

interface RequisitionItemsTableProps {
  rows: ItemRow[];
  selectedIds: Set<string>;
  onToggleSelect: (row: ItemRow) => void;
  onToggleAll: () => void;
  onStatusChange: (row: ItemRow, status: RequisitionItem['status']) => void;
  onEdit: (row: ItemRow) => void;
  onDelete: (row: ItemRow) => void;
  onAddItem: (row: ItemRow) => void;
  onEditRequisition: (row: ItemRow) => void;
  onDeleteRequisition: (row: ItemRow) => void;
  /** One payment order for the whole requisition this row belongs to */
  onCreatePaymentOrder: (row: ItemRow) => void;
}

const RequisitionItemsTable: React.FC<RequisitionItemsTableProps> = ({
  rows, selectedIds, onToggleSelect, onToggleAll, onStatusChange, onEdit, onDelete, onAddItem, onEditRequisition, onDeleteRequisition, onCreatePaymentOrder,
}) => {
  const selectableRows = rows.filter(r => !r.placeholder);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Rows organized into one collapsible section per vessel/HQ
  const groups = useMemo(() => {
    const map = new Map<string, ItemRow[]>();
    rows.forEach((r) => {
      const v = r.requisition.vessel || 'No Vessel';
      if (!map.has(v)) map.set(v, []);
      map.get(v)!.push(r);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const toggleGroup = (vessel: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(vessel)) next.delete(vessel); else next.add(vessel);
      return next;
    });
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input type="checkbox" checked={selectableRows.length > 0 && selectableRows.every(r => selectedIds.has(rowKey(r)))} onChange={onToggleAll} className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded" />
              </th>
              {['#', 'Item', 'Requisition', 'Qty', 'ROB', 'App Qty', 'Unit Price', 'Supplier', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groups.map(([vessel, groupRows]) => {
              const reqCount = new Set(groupRows.map(r => r.requisition.documentId || r.requisition.id)).size;
              const itemCount = groupRows.filter(r => !r.placeholder).length;
              return (
              <React.Fragment key={vessel}>
                <tr className="bg-gray-100 border-t-2 border-gray-200 cursor-pointer hover:bg-gray-200" onClick={() => toggleGroup(vessel)}>
                  <td colSpan={11} className="px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <span className="text-gray-500">{collapsed.has(vessel) ? '▸' : '▾'}</span>
                      <span>{vessel}</span>
                      <span className="font-normal text-xs text-gray-500">
                        {reqCount} requisition(s) · {itemCount} item(s)
                      </span>
                    </div>
                  </td>
                </tr>
                {!collapsed.has(vessel) && groupRows.map((row) => {
              const { item, requisition } = row;
              if (row.placeholder) return (
                <tr key={`${requisition.id}-${item.id}`} className="bg-gray-50">
                  <td className="px-4 py-4"></td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-400">-</td>
                  <td className="px-4 py-4 text-sm text-gray-500 italic">No items yet</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900"><div>{requisition.formNumber}</div><div className="text-xs text-gray-500">{requisition.vessel}</div></td>
                  <td className="px-4 py-4 text-sm text-gray-400" colSpan={5}>-</td>
                  <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(item.status)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-3">
                      <button onClick={() => onAddItem(row)} className="text-ocean-600 hover:text-ocean-800 font-medium text-xs">+ Add Item</button>
                      <button onClick={() => onEditRequisition(row)} className="text-blue-600 hover:text-blue-800 font-medium text-xs">Edit Req</button>
                      <button onClick={() => onDeleteRequisition(row)} className="text-red-600 hover:text-red-800 font-medium text-xs">Delete Req</button>
                    </div>
                  </td>
                </tr>
              );
              return (
                <tr key={rowKey(row)} className={selectedIds.has(rowKey(row)) ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <input type="checkbox" checked={selectedIds.has(rowKey(row))} onChange={() => onToggleSelect(row)} className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.itemNumber}</td>
                  <td className="px-4 py-4 text-sm text-gray-900"><div className="font-medium">{item.item}</div><div className="text-xs text-gray-500">{item.remarks}</div></td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900"><div>{requisition.formNumber}</div><div className="text-xs text-gray-500">{requisition.vessel}</div></td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.qty}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.rob}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.appQty}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{(Number(item.unitPrice) || 0).toFixed(2)} EGP</td>
                  <td className="px-4 py-4 text-sm text-gray-900">{item.supplier}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(item.status)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-col gap-2">
                      <select value={item.status} onChange={(e) => onStatusChange(row, e.target.value as RequisitionItem['status'])}
                        className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-ocean-500">
                        {Object.entries(statusConfig).map(([val, cfg]) => <option key={val} value={val}>{cfg.label}</option>)}
                      </select>
                      <div className="flex space-x-2">
                        <button onClick={() => onEdit(row)} className="text-blue-600 hover:text-blue-800 font-medium text-xs">Edit</button>
                        <button onClick={() => onDelete(row)} className="text-red-600 hover:text-red-800 font-medium text-xs">Delete</button>
                        <button onClick={() => onEditRequisition(row)} className="text-ocean-700 hover:text-ocean-900 font-medium text-xs">Edit Req</button>
                        <button onClick={() => onDeleteRequisition(row)} className="text-red-800 hover:text-red-900 font-medium text-xs">Delete Req</button>
                        <button onClick={() => onCreatePaymentOrder(row)} className="text-emerald-700 hover:text-emerald-900 font-medium text-xs">PO Req</button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
                })}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="text-center py-12"><p className="text-gray-500">No requisition items found. Create a requisition first.</p></div>
        )}
      </div>
    </div>
  );
};

export default RequisitionItemsTable;
